import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { LayoutDashboard, Briefcase, FileText, Users, LogOut, Globe, ChevronRight, Menu, X, DollarSign, BarChart3, Calendar, Send, Activity, CreditCard } from 'lucide-react';
import NotificationBell from './NotificationBell';
import ReminderAlert from './ReminderAlert';

const roleMenus = {
  admin: [
    { key: 'dashboard', icon: LayoutDashboard, path: '/admin' },
    { key: 'cases', icon: Briefcase, path: '/admin/cases' },
    { key: 'documents', icon: FileText, path: '/admin/documents' },
    { key: 'users', icon: Users, path: '/admin/users' },
    { key: 'invoices', icon: DollarSign, path: '/admin/invoices' },
    { key: 'payment_methods', icon: CreditCard, path: '/admin/payment-methods' },
    { key: 'reports', icon: BarChart3, path: '/admin/reports' },
    { key: 'reminders', icon: Calendar, path: '/admin/reminders' },
    { key: 'send_notification', icon: Send, path: '/admin/send-notification' },
    { key: 'activity_log', icon: Activity, path: '/admin/activity-log' },
  ],
  lawyer: [
    { key: 'dashboard', icon: LayoutDashboard, path: '/lawyer' },
    { key: 'my_cases', icon: Briefcase, path: '/lawyer/cases' },
    { key: 'documents', icon: FileText, path: '/lawyer/documents' },
    { key: 'invoices', icon: DollarSign, path: '/lawyer/invoices' },
    { key: 'reminders', icon: Calendar, path: '/lawyer/reminders' },
    { key: 'send_notification', icon: Send, path: '/lawyer/send-notification' },
  ],
  client: [
    { key: 'dashboard', icon: LayoutDashboard, path: '/client' },
    { key: 'my_cases', icon: Briefcase, path: '/client/cases' },
    { key: 'documents', icon: FileText, path: '/client/documents' },
    { key: 'invoices', icon: DollarSign, path: '/client/invoices' },
    { key: 'reminders', icon: Calendar, path: '/client/reminders' },
  ],
};

function SidebarContent({ onClose }) {
  const { user, logout } = useAuth();
  const { t, lang, toggleLang } = useLanguage();
  const location = useLocation();
  const role = user?.role || 'client';
  const menus = roleMenus[role] || roleMenus.client;

  return (
    <>
      {/* Logo */}
      <div className="p-4 border-b border-slate-100 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" onClick={onClose}>
          <img src="https://img1.wsimg.com/isteam/ip/e4bd2fa3-4e7e-4367-966e-734c45bd3de1/B679532D-818B-41E0-B271-6D14114F9E5C.png/:/rs=h:64,cg:true,m/qt=q:95" alt="Logo" className="h-8 w-auto" />
          <span className="text-sm font-bold text-[#0F172A] font-heading leading-tight">{t('firm_name_short')}</span>
        </Link>
        {onClose && (
          <button onClick={onClose} className="lg:hidden p-1 rounded hover:bg-slate-100" data-testid="close-sidebar">
            <X className="w-5 h-5 text-[#475569]" />
          </button>
        )}
      </div>

      {/* User info */}
      <div className="px-4 py-3 border-b border-slate-100">
        <p className="text-sm font-medium text-[#0F172A] truncate">{user?.name}</p>
        <p className="text-xs text-[#94A3B8] truncate">{user?.email || user?.phone}</p>
        <span className="inline-block mt-1 px-2 py-0.5 text-xs rounded-full bg-[#B48E4B]/10 text-[#B48E4B] capitalize font-medium">
          {t(role)}
        </span>
      </div>

      {/* Nav */}
      <nav className="flex-1 py-3 px-2 space-y-1">
        {menus.map(item => {
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.key}
              to={item.path}
              onClick={onClose}
              data-testid={`nav-${item.key}`}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                isActive ? 'bg-[#0F172A] text-white' : 'text-[#475569] hover:bg-slate-100 hover:text-[#0F172A]'
              }`}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              <span className="flex-1">{t(item.key)}</span>
              {isActive && <ChevronRight className="w-4 h-4" />}
            </Link>
          );
        })}
      </nav>

      {/* Bottom */}
      <div className="p-2 border-t border-slate-100 space-y-1">
        <button
          onClick={() => { toggleLang(); onClose?.(); }}
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-[#475569] hover:bg-slate-100 hover:text-[#0F172A] w-full transition-colors"
          data-testid="sidebar-lang-toggle"
        >
          <Globe className="w-5 h-5" />
          <span>{lang === 'en' ? 'العربية' : 'English'}</span>
        </button>
        <button
          onClick={() => { logout(); onClose?.(); }}
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-red-600 hover:bg-red-50 w-full transition-colors"
          data-testid="logout-button"
        >
          <LogOut className="w-5 h-5" />
          <span>{t('logout')}</span>
        </button>
      </div>
    </>
  );
}

export default function DashboardLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="flex min-h-screen bg-[#F1F5F9]" data-testid="dashboard-layout">
      <ReminderAlert />
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-60 xl:w-64 bg-white border-e border-slate-200 flex-col min-h-screen flex-shrink-0" data-testid="sidebar">
        <SidebarContent />
      </aside>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-black/40" onClick={() => setSidebarOpen(false)} />
          <aside className="fixed inset-y-0 start-0 w-72 bg-white flex flex-col z-50 shadow-xl" data-testid="mobile-sidebar">
            <SidebarContent onClose={() => setSidebarOpen(false)} />
          </aside>
        </div>
      )}

      {/* Main content */}
      <main className="flex-1 overflow-auto min-w-0">
        {/* Mobile header */}
        <div className="lg:hidden sticky top-0 z-40 bg-white/80 backdrop-blur-lg border-b border-slate-200 px-4 py-3 flex items-center gap-3">
          <button onClick={() => setSidebarOpen(true)} className="p-1.5 rounded-lg hover:bg-slate-100" data-testid="open-sidebar">
            <Menu className="w-5 h-5 text-[#0F172A]" />
          </button>
          <img src="https://img1.wsimg.com/isteam/ip/e4bd2fa3-4e7e-4367-966e-734c45bd3de1/B679532D-818B-41E0-B271-6D14114F9E5C.png/:/rs=h:64,cg:true,m/qt=q:95" alt="Logo" className="h-7 w-auto" />
          <div className="ms-auto">
            <NotificationBell testIdSuffix="-mobile" />
          </div>
        </div>
        <div className="p-4 sm:p-6 lg:p-8 max-w-7xl">
          {/* Desktop notification bar */}
          <div className="hidden lg:flex justify-end mb-4">
            <NotificationBell testIdSuffix="-desktop" />
          </div>
          {children}
        </div>
      </main>
    </div>
  );
}
